import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { FormProvider, useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';

import { DSelect, HInput, HTextarea, HCheckbox } from '../../../../validation/HForm';
import LoaderInbox from '../Loader/LoaderInbox';
import { createTransactionEditScheme } from '../../../../validation/scheme/transactionEditScheme';
import { getEmployee, resetHandbook } from '../../../../redux/actions/handbookAction';
import { editTransaction, resetTransaction } from '../../../../redux/actions/transactionsAction';
import { Message } from '../../../utilites';

import CrossIcon from '../../../../assets/icons/cross.svg';

const ModalInboxOperationContentEdit = ({
  closeModalEditTransaction,
  transactionToEdit
}) => {

  const { users, employeesError, usersLoading, reasonsLoading, reasonsError } =
    useSelector(({ handbook }) => handbook);
  const { lastTransaction, lastTransactionLoading, commonError, editTransaction: transactionResponse } = useSelector(
    ({ transactions }) => transactions,
  );

  const [transactionDoc, setTransactionDoc] = useState(transactionToEdit.documentNumber);
  const [employees, setEmployees] = useState([{ value: 0, label: null }]);
  const [disabled, setDisabled] = useState(false);
  const [defaultPassport, setDefaultPassport] = useState('');
  const hasAttachment = transactionToEdit.attachment && transactionToEdit.attachment !== undefined;
  const isRecalculationTransaction = transactionToEdit.section === 'CLOSURE_RECALCULATION' && hasAttachment;

  const form = useForm({
    mode: 'all',
    resolver: joiResolver(createTransactionEditScheme(transactionToEdit.type)),
  });

  const dispatch = useDispatch();

  const composeError = reasonsError || commonError || employeesError;

  useEffect(() => {
    if(transactionResponse) closeModalEditTransaction()
  }, [transactionResponse, closeModalEditTransaction]);

  useEffect(() => {
    if (transactionToEdit.accepted === 'физических лиц' || transactionToEdit.accepted === 'физическим лицам' || isRecalculationTransaction) {
          setDisabled(true)
    } else {
        dispatch(getEmployee({ bySearch: transactionToEdit.accepted.slice(0, 2), document: true }));
    }
  }, [transactionToEdit.accepted, dispatch]);

  useEffect(() => {
    if (users.length) {
      setEmployees((prev) => [prev[0], ...users.map((u) => ({ value: u.id, label: u.fullName }))]);
    } else {
      setEmployees((prev) => [prev[0]]);
    }
  }, [users]);

  useEffect(() => {
    if (transactionToEdit.type === 'DEBIT') {
      if (transactionToEdit.accepted === "физическим лицам") {
        setDefaultPassport(transactionToEdit?.passport);
      } else {
        const user = users.find((u) => u.fullName === transactionToEdit.accepted);
        const userDocument = transactionToEdit?.passport === user?.document ? user?.document : transactionToEdit?.passport
        setDefaultPassport(user ? userDocument : transactionToEdit?.passport);
      }
    }
  }, [users, form, transactionToEdit.accepted]);

  useEffect(() => {
    return () => {
      dispatch(resetHandbook());
      dispatch(resetTransaction())
    };
  }, [dispatch]);

  const handleGetEmployee = (e, action) => {
    if (action.action === 'input-change' && e.length >= 2) {
      dispatch(getEmployee({ bySearch: e, document: true }));
    }

    setEmployees((prev) => [{ value: 0, label: e }, ...prev.slice(1)]);
  };

  const handleChoose = (i) => {
    if (transactionToEdit.type !== 'CREDIT') {
      const passport = users.find((u) => u.id === i.value)?.document;
      form.setValue('passport', passport || '');
    }
  };

  const handleChange = () => {
    const value = form.getValues();
    if (value.toPerson) {
      if (transactionToEdit.type === 'CREDIT') {
        form.setValue(
          'accepted',
          { value: 0, label: 'физических лиц' },
          { shouldValidate: true },
        );
        setDisabled(true);
      } else {
        form.setValue(
          'accepted',
          { value: 0, label: 'физическим лицам' },
          { shouldValidate: true },
        );
        setDisabled(true);
      }
    } else {
      form.setValue('accepted', [{ value: 0, label: null }]);
      setDisabled(false);
    }
  };

  const documentNumberChange = (e) => {
    setTransactionDoc(e.target.value);
  };

  const onTransactionEditSubmit = () => {
    let accepted = transactionToEdit.type === 'CREDIT' ? 'физических лиц' : 'физическим лицам'
    let data = form.getValues()
    data.id = transactionToEdit.id
    data.type = transactionToEdit.type
    if (isRecalculationTransaction) {
      accepted = data.accepted.label;
    }
    
    const reportData = {
      ...data,
      additionalInfo: data.attachment,
      attachment: undefined,
    };

    const payload = {
      data: {
        documentNumber: data.documentNumber,
        description: isRecalculationTransaction ? transactionToEdit.description : data.description,
        accepted: data.toPerson === true ? accepted : data.accepted.label,
        id: data.id,
        passport: data.passport,
        additionalInfo: data.attachment,
        ...(isRecalculationTransaction && { attachment: data.description }),
      },
      reportData
    }
    dispatch(editTransaction(payload));
  }

  return (
    <>
      {composeError && <Message type="error" text={composeError} />}
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onTransactionEditSubmit)}>
          <div className="modal-header">
            <h2>
              {transactionToEdit.type === 'CREDIT' ? 'Приходная' : 'Расходная'} операция №
              {transactionDoc ? transactionDoc : 1}
            </h2>
            <button
              className="button-icontrigger modal-closetrigger"
              onClick={closeModalEditTransaction}>
              <span className="icon">
                <CrossIcon />
              </span>
            </button>
          </div>
          <div className="modal-body">
            {reasonsLoading || lastTransactionLoading ? (
              <LoaderInbox />
            ) : (
              <div>
                <fieldset>
                  <div className="modal-subdivision">
                    <span className="label">{transactionToEdit.cashBoxName}</span>
                  </div>
                  <div className="flex-start align-baseline flex-space-between">
                    {lastTransaction && (
                      <span className="modal-expenditure-transaction">
                        <big>
                          {`Предыдущий №${lastTransaction.documentNumber} от 
                          ${moment(lastTransaction.date).format('DD.MM.YYYY')}
                          ${lastTransaction.user ? ',  ' + lastTransaction.user.fullName : ''}
                          `}
                        </big>
                      </span>
                    )}
                  </div>
                </fieldset>
                <fieldset>
                  <div className="form-row">
                    <div className="form-row-item">
                      <div className="form-element">
                        <label className="form-element-label" htmlFor="">
                          Номер документа
                        </label>
                        <div className="form-element-data">
                          <HInput
                            h="true"
                            className="input"
                            type="number"
                            name="documentNumber"
                            id="documentNumber"
                            onChange={documentNumberChange}
                            defaultValue={transactionToEdit.documentNumber}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="form-row-item">
                      <div className="form-element">
                        <label className="form-element-label" htmlFor="toPerson">
                          {transactionToEdit.type === 'CREDIT' ? 'От физических лиц' : 'Физическим лицам'}
                        </label>
                        <div className="form-element-data">
                          <HCheckbox
                            h="true"
                            name="toPerson"
                            className="input"
                            id="toPerson"
                            onChange={handleChange}
                            defaultValue = {
                              transactionToEdit.accepted === 'физических лиц'
                              || transactionToEdit.accepted === 'физическим лицам'
                              || isRecalculationTransaction
                            }
                            disabled={isRecalculationTransaction}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-row-item">
                      <div className="form-element">
                        <label className="form-element-label" htmlFor="#">
                          {transactionToEdit.type === 'CREDIT' ? 'Принято от' : 'Выдать'}
                        </label>
                        <div className="form-element-data combobox-lg">
                          <DSelect
                            h="true"
                            name="accepted"
                            options={employees}
                            handleOptions={handleGetEmployee}
                            handleOnChange={handleChoose}
                            isLoading={usersLoading}
                            placeholder="Введите фамилию"
                            isDisabled={disabled}
                            isClearable={disabled}
                            defaultValue={
                              transactionToEdit.accepted === 'от физических лиц'
                              || transactionToEdit.accepted === 'физическим лицам'
                              ? { value: 0,
                                  label: transactionToEdit.type === 'CREDIT' ? 'от физических лиц' : 'физическим лицам' }
                              : { value: 0, label: transactionToEdit.accepted }
                            }
                          />
                        </div>
                      </div>
                    </div>
                    {
                    transactionToEdit.type === 'DEBIT' &&
                    (
                      <div className="form-row-item">
                        <div className="form-element">
                          <label className="form-element-label" htmlFor="#">
                            Паспортные данные
                          </label>
                          <div className="form-element-data">
                            <HInput
                              h="true"
                              className="input"
                              type="text"
                              name="passport"
                              id="passport"
                              defaultValue={defaultPassport}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="form-element">
                    <label className="form-element-label" htmlFor="#">
                      Приложение
                    </label>
                    <div className="form-element-data textarea">
                      <HTextarea
                        h="true"
                        className="input"
                        rows="4"
                        cols="10"
                        name="description"
                        id="description"
                        defaultValue={isRecalculationTransaction ? transactionToEdit.attachment : transactionToEdit.description}
                      />
                    </div>
                  </div>
                  <div className="form-element">
                    <label className="form-element-label" htmlFor="#">
                      Дополнительная информация
                    </label>
                    <div className="form-element-data textarea">
                      <HTextarea
                        h="true"
                        className="input"
                        rows="4"
                        cols="10"
                        name="attachment"
                        id="attachment"
                        defaultValue={transactionToEdit.additionalInfo}
                      />
                    </div>
                  </div>
                </fieldset>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button className="button">Сохранить</button>
            <button className="button-light" onClick={closeModalEditTransaction}>
              Отменить
            </button>
          </div>
        </form>
      </FormProvider>
    </>
  );
};

export default ModalInboxOperationContentEdit;
